import { motion } from 'framer-motion';
import {
    Check,
    CheckCheck,
    CheckSquare,
    CircleSlash,
    Eye,
    FileSearch,
    Filter,
    IndianRupee,
    LoaderCircle,
    MinusSquare,
    Search,
    Square,
    Sparkles,
    Timer,
    X,
} from 'lucide-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import styles from './ExpensesReviewQueue.module.css';

const REVIEW_REASON_CHIPS = [
    'Amount mismatch',
    'Wrong category',
    'Missing invoice attachment',
    'Duplicate entry',
    'Needs more information',
    'Month-end budget exceeded',
    'Incorrect vendor',
    'Resubmit next cycle',
];

const parseActiveStore = (activeStore) => {
    try {
        return (activeStore && activeStore.trim() !== '') ? JSON.parse(activeStore) : null;
    } catch {
        return null;
    }
};

const getPendingHours = (createdOn) => {
    if (!createdOn) return 0;
    const parsed = new Date(String(createdOn).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return 0;
    const diffMs = Date.now() - parsed.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
};

const normalizeQueueRow = (expense) => {
    const totalAmount = Number(expense?.totalExpense || 0);
    const createdOn = String(expense?.createdOn || '');

    return {
        expenseIdentifier: expense?.expenseIdentifier || '',
        vendorName: expense?.vendorName || expense?.vendorIdentifier || '—',
        vendorIdentifier: expense?.vendorIdentifier || '',
        billDate: expense?.expenseDate || '',
        invoiceNumber: expense?.vendorInvoiceNumber || '—',
        totalAmount,
        gstAmount: Number(expense?.gstAmount || 0),
        totalItems: Number(expense?.totalItems || 0),
        itemCategoriesSummary: expense?.categoriesSummary || expense?.expenseNotes || '—',
        paymentStatus: expense?.paymentStatus || 'unpaid',
        approvalStatus: expense?.approvalStatus || 'pending',
        expenseType: expense?.expenseType || 'opex',
        loggedByName: expense?.createdBy || '—',
        loggedAt: createdOn || '—',
        loggedFromDevice: expense?.createdFromDevice || '—',
        pendingHours: getPendingHours(createdOn),
        canQuickApprove: totalAmount <= 10000,
    };
};

const fmtCurrency = (value) => Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SORT_OPTIONS = [
    { value: 'pendingHours', label: 'Most Pending First' },
    { value: 'amountDesc', label: 'Amount High → Low' },
    { value: 'amountAsc', label: 'Amount Low → High' },
    { value: 'billDateDesc', label: 'Latest Bill Date' },
];

const getSelectStyles = () => ({
    control: (base, state) => ({
        ...base,
        width: '100%',
        boxSizing: 'border-box',
        minHeight: 35,
        borderRadius: 8,
        border: '1.5px solid #e2e8f0',
        borderColor: state.isFocused ? '#6d28d9' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(109, 40, 217, 0.09)' : 'none',
        backgroundColor: '#ffffff',
        '&:hover': {
            borderColor: state.isFocused ? '#6d28d9' : '#d1dae6',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 9px',
        fontSize: '0.79rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#0f172a',
        fontSize: '0.79rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#94a3b8',
        fontSize: '0.79rem',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: 33,
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#94a3b8',
        padding: 6,
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    menu: (base) => ({
        ...base,
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        overflow: 'hidden',
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '0.78rem',
        backgroundColor: state.isSelected ? '#ede9fe' : state.isFocused ? '#f5f3ff' : '#ffffff',
        color: state.isSelected ? '#5b21b6' : '#334155',
    }),
});

const notifyExpenseMutation = () => {
    window.dispatchEvent(new CustomEvent('expenses-dashboard:refresh'));
};

const formatBillDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ExpensesReviewQueue = () => {
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = parseActiveStore(asCtx?.activeStore);
    const storeIdentifier = activeStore?.storeIdentifier || '';

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [maxAmount, setMaxAmount] = useState('');
    const [onlyHighValue, setOnlyHighValue] = useState(false);
    const [sortBy, setSortBy] = useState('pendingHours');
    const [isProcessing, setIsProcessing] = useState(false);

    const [rejectModal, setRejectModal] = useState({
        isOpen: false,
        targetIds: [],
        reason: '',
    });

    const [partialModal, setPartialModal] = useState({
        isOpen: false,
        failedItems: [],
    });

    const fetchReviewQueue = useCallback(async () => {
        if (!storeIdentifier) {
            setRows([]);
            setErrorMessage('No store selected.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await fetch(BASE_URL_API + 'fetchExpensesList', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    pageSize: 200,
                    sort: { sortBy: 'createdOn', sortOrder: 'DESC' },
                    filters: { approvalStatus: 'pending' },
                }),
            });

            const result = await response.json();

            if (result?.status === 'success') {
                const list = Array.isArray(result?.response?.expenses) ? result.response.expenses : [];
                setRows(list.map(normalizeQueueRow));
                setSelectedIds([]);
                return;
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout();
                return;
            }

            setRows([]);
            setErrorMessage(result?.message || 'Unable to fetch review queue.');
        } catch {
            setRows([]);
            setErrorMessage('Unable to connect to server.');
        } finally {
            setIsLoading(false);
        }
    }, [authCtx, storeIdentifier]);

    useEffect(() => {
        fetchReviewQueue();
    }, [fetchReviewQueue]);

    const filteredRows = useMemo(() => {
        const min = minAmount === '' ? null : Number(minAmount);
        const max = maxAmount === '' ? null : Number(maxAmount);
        const q = search.trim().toLowerCase();

        let data = rows.filter((row) => {
            if (q) {
                const haystack = [
                    row.expenseIdentifier,
                    row.vendorName,
                    row.invoiceNumber,
                    row.loggedByName,
                    row.itemCategoriesSummary,
                ].join(' ').toLowerCase();
                if (!haystack.includes(q)) return false;
            }

            if (min !== null && row.totalAmount < min) return false;
            if (max !== null && row.totalAmount > max) return false;
            if (onlyHighValue && row.totalAmount < 10000) return false;
            return true;
        });

        data = [...data].sort((a, b) => {
            if (sortBy === 'amountDesc') return b.totalAmount - a.totalAmount;
            if (sortBy === 'amountAsc') return a.totalAmount - b.totalAmount;
            if (sortBy === 'billDateDesc') return new Date(b.billDate) - new Date(a.billDate);
            return b.pendingHours - a.pendingHours;
        });

        return data;
    }, [rows, search, minAmount, maxAmount, onlyHighValue, sortBy]);

    const selectedInViewCount = useMemo(() => {
        const visibleSet = new Set(filteredRows.map((item) => item.expenseIdentifier));
        return selectedIds.filter((id) => visibleSet.has(id)).length;
    }, [filteredRows, selectedIds]);

    const summary = useMemo(() => {
        const totalPendingAmount = filteredRows.reduce((sum, row) => sum + Number(row.totalAmount || 0), 0);
        const agingCritical = filteredRows.filter((row) => row.pendingHours >= 72).length;
        return {
            totalPending: filteredRows.length,
            totalPendingAmount,
            agingCritical,
        };
    }, [filteredRows]);

    const toggleSelect = (expenseId) => {
        setSelectedIds((prev) => (prev.includes(expenseId) ? prev.filter((id) => id !== expenseId) : [...prev, expenseId]));
    };

    const toggleSelectAllVisible = () => {
        const visibleIds = filteredRows.map((row) => row.expenseIdentifier);
        const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

        if (allSelected) {
            setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
            return;
        }

        setSelectedIds((prev) => {
            const set = new Set(prev);
            visibleIds.forEach((id) => set.add(id));
            return Array.from(set);
        });
    };

    const getAuthIdentifier = () => {
        try {
            return authCtx?.authIdentifier || ''
        } catch {
            return '';
        }
    };

    const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size));
        }
        return chunks;
    };

    const callReviewExpense = async (payload) => {
        const response = await fetch(BASE_URL_API + 'reviewExpense', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        return response.json();
    };

    const quickApprove = async (expenseId) => {
        const authIdentifier = getAuthIdentifier();
        if (!authIdentifier) {
            toast.error('Session error. Please log in again.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        setIsProcessing(true);
        try {
            const result = await callReviewExpense({
                token: authCtx?.token,
                storeIdentifier,
                authIdentifier,
                expenseIdentifier: expenseId,
                decision: 'approved',
            });

            if (result?.message === 'tokenExpired') {
                authCtx.logout();
                return;
            }

            if (result?.status === 'success') {
                toast.success('Expense approved.', { position: 'top-center', hideProgressBar: true, autoClose: 2000 });
                notifyExpenseMutation();
                await fetchReviewQueue();
                return;
            }

            toast.error(result?.message || 'Failed to approve expense.', { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
        } catch {
            toast.error('Unable to connect to server.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
        } finally {
            setIsProcessing(false);
        }
    };

    const approveSelected = async () => {
        if (selectedIds.length === 0) return;
        const authIdentifier = getAuthIdentifier();
        if (!authIdentifier) {
            toast.error('Session error. Please log in again.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        setIsProcessing(true);
        const batches = chunkArray(selectedIds, 100);
        let totalSucceeded = 0;
        const allFailed = [];

        try {
            for (const batch of batches) {
                const result = await callReviewExpense({
                    token: authCtx?.token,
                    storeIdentifier,
                    authIdentifier,
                    expenseIdentifiers: batch,
                    decision: 'approved',
                });

                if (result?.message === 'tokenExpired') {
                    authCtx.logout();
                    return;
                }

                if (result?.status === 'success') {
                    totalSucceeded += batch.length;
                } else if (result?.status === 'partial') {
                    const results = Array.isArray(result?.response?.results) ? result.response.results : [];
                    const succeeded = results.filter((r) => r?.status === 'success').length;
                    const failed = results.filter((r) => r?.status !== 'success');
                    totalSucceeded += succeeded;
                    allFailed.push(...failed);
                } else {
                    toast.error(result?.message || 'Batch approval failed.', { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
                }
            }

            if (allFailed.length > 0) {
                setPartialModal({ isOpen: true, failedItems: allFailed });
            }

            if (totalSucceeded > 0) {
                toast.success(`${totalSucceeded} expense(s) approved.`, { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
                notifyExpenseMutation();
            }

            await fetchReviewQueue();
        } catch {
            toast.error('Unable to connect to server.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
        } finally {
            setIsProcessing(false);
        }
    };

    const openRejectModal = (targetIds) => {
        setRejectModal({ isOpen: true, targetIds, reason: '' });
    };

    const confirmReject = async () => {
        const reason = rejectModal.reason.trim();
        if (!reason) {
            toast.error('Rejection reason is required.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        const authIdentifier = getAuthIdentifier();
        if (!authIdentifier) {
            toast.error('Session error. Please log in again.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        setIsProcessing(true);
        const batches = chunkArray(rejectModal.targetIds, 100);
        let totalSucceeded = 0;
        const allFailed = [];

        try {
            for (const batch of batches) {
                const result = await callReviewExpense({
                    token: authCtx?.token,
                    storeIdentifier,
                    authIdentifier,
                    expenseIdentifiers: batch,
                    decision: 'rejected',
                    reason,
                });

                if (result?.message === 'tokenExpired') {
                    authCtx.logout();
                    return;
                }

                if (result?.status === 'success') {
                    totalSucceeded += batch.length;
                } else if (result?.status === 'partial') {
                    const results = Array.isArray(result?.response?.results) ? result.response.results : [];
                    const succeeded = results.filter((r) => r?.status === 'success').length;
                    const failed = results.filter((r) => r?.status !== 'success');
                    totalSucceeded += succeeded;
                    allFailed.push(...failed);
                } else {
                    toast.error(result?.message || 'Batch rejection failed.', { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
                }
            }

            if (allFailed.length > 0) {
                setPartialModal({ isOpen: true, failedItems: allFailed });
            }

            if (totalSucceeded > 0) {
                toast.success(`${totalSucceeded} expense(s) rejected.`, { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
                notifyExpenseMutation();
            }

            setRejectModal({ isOpen: false, targetIds: [], reason: '' });
            await fetchReviewQueue();
        } catch {
            toast.error('Unable to connect to server.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
        } finally {
            setIsProcessing(false);
        }
    };

    const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((row) => selectedIds.includes(row.expenseIdentifier));
    const hasSelectedRows = selectedIds.length > 0;
    const isPartiallySelected = selectedInViewCount > 0 && !allVisibleSelected;
    const selectAllIcon = allVisibleSelected ? CheckSquare : isPartiallySelected ? MinusSquare : Square;
    const SelectAllIcon = selectAllIcon;

    return (
        <div className={styles.page}>
            <motion.section className={styles.hero} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}>
                <div className={styles.heroTop}>
                    <span className={styles.pill}><Sparkles size={13} /> Ops Approval Queue</span>
                    <span className={styles.meta}>Live list via fetchExpensesList</span>
                </div>
                <h1>Expenses Review Queue</h1>
                <p>Scan pending bills fast, bulk approve routine expenses, and open detailed review for sensitive bills.</p>

                <div className={styles.stats}>
                    <div className={styles.stat}><FileSearch size={14} /> Pending: <strong>{summary.totalPending}</strong></div>
                    <div className={styles.stat}><IndianRupee size={14} /> Value: <strong>₹{fmtCurrency(summary.totalPendingAmount)}</strong></div>
                    <div className={styles.stat}><Timer size={14} /> Aging 72h+: <strong>{summary.agingCritical}</strong></div>
                    <div className={styles.stat}><CheckCheck size={14} /> Selected: <strong>{selectedInViewCount}</strong></div>
                </div>
            </motion.section>

            <section className={styles.panel}>
                <div className={styles.panelHead}>
                    <div className={styles.panelTitle}><Filter size={13} /> Filter & Approve Controls</div>
                    <div className={styles.bulkActions}>
                        <button type="button" className={styles.selectAllBtn} onClick={fetchReviewQueue} disabled={isLoading || isProcessing}>
                            {isLoading ? <LoaderCircle size={13} className={styles.spin} /> : 'Refresh'}
                        </button>
                        <button type="button" className={styles.approveBtn} onClick={approveSelected} disabled={!hasSelectedRows || isProcessing}>
                            {isProcessing ? <LoaderCircle size={13} className={styles.spin} /> : <Check size={13} />} Approve Selected
                        </button>
                        <button
                            type="button"
                            className={styles.rejectBtn}
                            onClick={() => openRejectModal(selectedIds)}
                            disabled={!hasSelectedRows || isProcessing}
                        >
                            <X size={13} /> Reject Selected
                        </button>
                    </div>
                </div>

                <div className={styles.filtersGrid}>
                    <label>
                        <span><Search size={12} /> Search</span>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Expense id, vendor, invoice, manager" />
                    </label>
                    <label>
                        <span>Min Amount (₹)</span>
                        <input type="number" min="0" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} placeholder="0" />
                    </label>
                    <label>
                        <span>Max Amount (₹)</span>
                        <input type="number" min="0" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} placeholder="100000" />
                    </label>
                    <label>
                        <span>Sort</span>
                        <Select
                            options={SORT_OPTIONS}
                            value={SORT_OPTIONS.find((option) => option.value === sortBy) || null}
                            onChange={(option) => setSortBy(option?.value || 'pendingHours')}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                            isSearchable={false}
                        />
                    </label>
                    <label className={styles.switchLabel}>
                        <span>Only High Value (₹10k+)</span>
                        <button type="button" className={`${styles.switch} ${onlyHighValue ? styles.switchOn : ''}`} onClick={() => setOnlyHighValue((prev) => !prev)}>
                            <span />
                        </button>
                    </label>
                </div>
            </section>

            <section className={styles.tableWrap}>
                <div className={styles.tableHeadRow}>
                    <button type="button" className={styles.selectAllBtn} onClick={toggleSelectAllVisible}>
                        <SelectAllIcon size={14} /> {allVisibleSelected ? 'Unselect All' : 'Select All'}
                    </button>
                    <span>{filteredRows.length} record(s)</span>
                </div>

                <div className={styles.tableScroll}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th></th>
                                <th>Vendor</th>
                                <th>Bill Date</th>
                                <th>Total</th>
                                <th>Items</th>
                                <th>Logged By</th>
                                <th>Pending</th>
                                <th>Category Summary</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td colSpan={9} className={styles.emptyCell}>
                                        <div className={styles.emptyCellContent}><LoaderCircle size={16} className={styles.spin} /> Loading pending queue...</div>
                                    </td>
                                </tr>
                            )}

                            {!isLoading && errorMessage && (
                                <tr>
                                    <td colSpan={9} className={styles.emptyCell}>
                                        <div className={styles.emptyCellContent}><CircleSlash size={16} /> {errorMessage}</div>
                                    </td>
                                </tr>
                            )}

                            {!isLoading && !errorMessage && filteredRows.length === 0 && (
                                <tr>
                                    <td colSpan={9} className={styles.emptyCell}>
                                        <div className={styles.emptyCellContent}><CircleSlash size={16} /> No pending expenses match current filters.</div>
                                    </td>
                                </tr>
                            )}

                            {!isLoading && !errorMessage && filteredRows.map((row) => (
                                <tr key={row.expenseIdentifier}>
                                    <td>
                                        <button
                                            type="button"
                                            className={styles.rowSelectBtn}
                                            onClick={() => toggleSelect(row.expenseIdentifier)}
                                            aria-label={selectedIds.includes(row.expenseIdentifier) ? 'Deselect row' : 'Select row'}
                                        >
                                            {selectedIds.includes(row.expenseIdentifier)
                                                ? <CheckSquare size={16} strokeWidth={1.9} />
                                                : <Square size={16} strokeWidth={1.9} />}
                                        </button>
                                    </td>
                                    <td>
                                        <div className={styles.vendorCell}>
                                            <strong>{row.vendorName}</strong>
                                            <span>{row.invoiceNumber}</span>
                                        </div>
                                    </td>
                                    <td>{formatBillDate(row.billDate)}</td>
                                    <td className={styles.money}>₹{fmtCurrency(row.totalAmount)}</td>
                                    <td>{row.totalItems}</td>
                                    <td>{row.loggedByName}</td>
                                    <td><span className={styles.pendingChip}>{row.pendingHours}h</span></td>
                                    <td>{row.itemCategoriesSummary}</td>
                                    <td>
                                        <div className={styles.rowActions}>
                                            {row.canQuickApprove && (
                                                <button type="button" className={styles.quickApprove} onClick={() => quickApprove(row.expenseIdentifier)} disabled={isProcessing}>
                                                    <Check size={12} /> Quick Approve
                                                </button>
                                            )}
                                            <Link className={styles.reviewLink} to={`/expenses/review/${row.expenseIdentifier}`}><Eye size={12} /> Open Review</Link>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {partialModal.isOpen && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.modalCard}>
                        <h3>Partial Success</h3>
                        <p>Some expenses could not be processed. The following items failed:</p>
                        <div className={styles.partialFailList}>
                            {partialModal.failedItems.map((item, idx) => (
                                <div key={item?.expenseIdentifier || idx} className={styles.partialFailItem}>
                                    <strong>{item?.expenseIdentifier || `Item ${idx + 1}`}</strong>
                                    {item?.message && <span>{item.message}</span>}
                                </div>
                            ))}
                        </div>
                        <div className={styles.modalActions}>
                            <button type="button" className={styles.cancelBtn} onClick={() => setPartialModal({ isOpen: false, failedItems: [] })}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {rejectModal.isOpen && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.modalCard}>
                        <h3>Reject Expense(s)</h3>
                        <p>Reason is mandatory for rejection.</p>

                        <div className={styles.reasonChips}>
                            {REVIEW_REASON_CHIPS.map((reason) => (
                                <button
                                    key={reason}
                                    type="button"
                                    className={`${styles.reasonChip} ${rejectModal.reason === reason ? styles.reasonChipActive : ''}`}
                                    onClick={() => setRejectModal((prev) => ({ ...prev, reason }))}
                                >
                                    {reason}
                                </button>
                            ))}
                        </div>

                        <textarea
                            value={rejectModal.reason}
                            onChange={(e) => setRejectModal((prev) => ({ ...prev, reason: e.target.value }))}
                            placeholder="Add rejection note"
                            rows={3}
                        />

                        <div className={styles.modalActions}>
                            <button type="button" className={styles.cancelBtn} onClick={() => setRejectModal({ isOpen: false, targetIds: [], reason: '' })}>Cancel</button>
                            <button type="button" className={styles.confirmRejectBtn} onClick={confirmReject} disabled={isProcessing}>Reject</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpensesReviewQueue;
